// =============================================================================
// DisplayBoardScreen — /display — the Live Board / newsroom (mig 349).
//
// Full-screen TV surface: renders whatever widget layout a manager PUBLISHED
// on /display/edit. Reads the same tables as the rest of Ops (projects,
// crew assignments, programs, key dates, notices) — nothing entered twice.
//
// Rendered OUTSIDE AppShell (no sidebar/topbar), with its OWN fixed dark
// palette — the board is one deliberate look regardless of app theme
// (James: "keeping the theme and style the same").
//
// Data refreshes itself every 5 minutes; clock ticks every second; leave it
// running on the office TV. TIMEZONE: local day keys only.
// =============================================================================

import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { format } from "date-fns";

// ---- board palette (fixed — not app theme tokens, on purpose) ----
const C = {
  bg: "#0d1013", card: "#171d24", cardBorder: "#2a323b", line: "#222a33",
  text: "#eef1f4", mut: "#98a3b0", dim: "#5c6670", accent: "#FF7844",
};

// One card look everywhere — gradient sheen, real border, depth.
const CARD: React.CSSProperties = {
  background: "linear-gradient(180deg, #1b222b, #151a20)",
  border: "1px solid #2a323b",
  borderRadius: 16,
  boxShadow: "0 8px 24px rgba(0,0,0,.4)",
};

const pad2 = (n: number) => String(n).padStart(2, "0");
const localKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const keyToDate = (k: string) => { const [y, m, d] = k.split("-").map(Number); return new Date(y, m - 1, d); };
const addDaysKey = (k: string, n: number) => { const d = keyToDate(k); d.setDate(d.getDate() + n); return localKey(d); };

const NOT_RUNNING = new Set(["closed", "cancelled", "lost", "acceptance_pending", "on_hold"]);

interface LayoutItem { id: string; widget: string; span: number; offset?: number }

// -----------------------------------------------------------------------------
export default function DisplayBoardScreen() {
  const user = useRequireAuth();
  const todayK = localKey(new Date());

  // one bundle, refreshed every 5 min — the TV never needs a reload
  const { data } = useQuery({
    queryKey: ["display_board_bundle"],
    refetchInterval: 5 * 60 * 1000,
    queryFn: async () => {
      const weekStart = (() => { const d = new Date(); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return localKey(d); })();
      const horizon = addDaysKey(todayK, 45);
      const monthStartK = todayK.slice(0, 8) + "01";
      const [boardQ, projQ, pcaQ, crewsQ, actsQ, subsQ, noticesQ, outagesQ, plantQ, defectsQ, incidentsQ] = await Promise.all([
        (supabase as any).from("display_board").select("*").limit(1).maybeSingle(),
        (supabase as any).from("projects")
          .select("project_id, project_code, project_name, status, sites:site_id(site_name)")
          .in("status", ["live", "mobilising"]),
        (supabase as any).from("project_crew_assignments")
          .select("pca_id, project_id, assigned_from, assigned_to, projects:project_id(project_code, project_name, status, sites:site_id(site_name)), crews:crew_id(crew_id, crew_code, crew_name, colour_hex)")
          .or(`assigned_to.is.null,assigned_to.gte.${weekStart}`),
        (supabase as any).from("crews").select("crew_id, crew_code, crew_name, colour_hex, active").eq("active", true),
        (supabase as any).from("project_program_activities")
          .select("activity_id, project_id, name, kind, start_date, end_date, is_key, in_build_plan, plant_needed, sort_order")
          .eq("is_active", true).not("start_date", "is", null)
          // end_date is nullable — a single-day line that never got one was
          // silently dropped from the board by the old `gte("end_date", …)`.
          // Every other screen defaults it to start_date; so does this now.
          .lte("start_date", horizon)
          .or(`end_date.gte.${addDaysKey(todayK, -1)},and(end_date.is.null,start_date.gte.${addDaysKey(todayK, -1)})`),
        // Only subs that belong to a LIVE line that is actually on the crew's
        // build plan — the board says "plan ticked", so it must count the plan
        // and nothing else. Unfiltered, archived revisions inflated the bar.
        (supabase as any).from("project_program_subactivities")
          .select("project_id, done, activity:activity_id!inner(is_active, in_build_plan)")
          .eq("activity.is_active", true).eq("activity.in_build_plan", true),
        (supabase as any).from("display_notices").select("*").eq("is_active", true)
          .order("sort_order").order("created_at"),
        (supabase as any).from("project_bookings")
          .select("project_id, title, booking_type, scheduled_start, scheduled_end, status")
          .eq("booking_type", "outage").neq("status", "cancelled")
          .gte("scheduled_start", new Date().toISOString())
          .lte("scheduled_start", new Date(Date.now() + 15 * 86400000).toISOString())
          .order("scheduled_start"),
        (supabase as any).from("project_bookings")
          .select("project_id, title, booking_type, scheduled_start, scheduled_end, status, external_contact_name")
          .eq("booking_type", "plant").neq("status", "cancelled")
          .gte("scheduled_start", new Date(Date.now() - 86400000).toISOString())
          .lte("scheduled_start", new Date(Date.now() + 15 * 86400000).toISOString())
          .order("scheduled_start"),
        (supabase as any).from("site_defects")
          .select("defect_id, status, raised_at, closed_at")
          .or(`status.not.in.(closed,cancelled),raised_at.gte.${addDaysKey(todayK, -90)}`),
        (supabase as any).from("incidents")
          .select("incident_id, occurred_at, status, severity")
          .gte("occurred_at", new Date(Date.now() - 365 * 86400000).toISOString())
          .order("occurred_at", { ascending: false }),
      ]);
      void monthStartK;
      // A TV board must never blank because ONE widget's query hiccuped —
      // log the failure, render everything else. (4 Sep: a bad crews column
      // name took the whole board down.)
      for (const q of [boardQ, projQ, pcaQ, crewsQ, actsQ, subsQ, noticesQ, outagesQ, plantQ, defectsQ, incidentsQ]) {
        if (q.error) console.error("Live Board query failed:", q.error);
      }
      return {
        board: boardQ.data, projects: projQ.data ?? [], pcas: pcaQ.data ?? [],
        crews: crewsQ.data ?? [],
        acts: actsQ.data ?? [], subs: subsQ.data ?? [],
        notices: noticesQ.data ?? [], outages: outagesQ.data ?? [],
        plant: plantQ.data ?? [],
        defects: defectsQ.data ?? [], incidents: incidentsQ.data ?? [],
      };
    },
  });

  // ticking clock
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  if (!user) return null;

  const layout: LayoutItem[] = (data?.board?.published_layout ?? []) as LayoutItem[];
  const notices = (data?.notices ?? []).filter((n: any) => {
    if (n.active_from && n.active_from > todayK) return false;
    if (n.active_to && n.active_to < todayK) return false;
    return true;
  });

  // ticker: manager items + auto (key dates ≤7d, outages ≤7d)
  const tickerItems: string[] = [];
  for (const n of notices.filter((x: any) => x.kind === "ticker")) tickerItems.push(n.body);
  const week7 = addDaysKey(todayK, 7);
  const projName = (pid: string) => {
    const p = (data?.projects ?? []).find((x: any) => x.project_id === pid);
    return p?.sites?.site_name ?? p?.project_name ?? "";
  };
  for (const a of (data?.acts ?? []).filter((x: any) => x.is_key && x.start_date >= todayK && x.start_date <= week7)) {
    const nm = projName(a.project_id);
    if (nm) tickerItems.push(`★ ${nm} — ${a.name} ${format(keyToDate(a.start_date), "EEE d MMM")}`);
  }
  for (const o of (data?.outages ?? []).filter((x: any) => localKey(new Date(x.scheduled_start)) <= week7)) {
    const nm = projName(o.project_id);
    if (nm) tickerItems.push(`🌙 ${nm} — ${o.title} ${format(new Date(o.scheduled_start), "EEE HH:mm")}`);
  }
  if (tickerItems.length === 0) tickerItems.push("Coretel Group — staying connected");

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, display: "flex", flexDirection: "column", fontFamily: "'Segoe UI', -apple-system, Roboto, sans-serif" }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 48px", borderBottom: `1px solid ${C.cardBorder}`, background: "linear-gradient(180deg, #12161b, transparent)" }}>
        <img src="/coretel-logo.svg" alt="Coretel Group" style={{ height: 86 }} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 14, letterSpacing: 6, color: C.mut, textTransform: "uppercase", fontWeight: 600 }}>Operations board</div>
          <div style={{ fontSize: 12, color: C.dim, marginTop: 4, letterSpacing: 2 }}>LIVE · refreshes itself</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 48, fontWeight: 200, letterSpacing: 2, fontVariantNumeric: "tabular-nums" }}>{format(now, "HH:mm")}</div>
          <div style={{ fontSize: 13, color: C.mut, letterSpacing: 1 }}>{format(now, "EEEE d MMMM")}</div>
        </div>
      </div>

      {/* widget grid */}
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gridAutoRows: ROW_UNIT, gap: ROW_GAP, gridAutoFlow: "row dense", padding: "26px 48px 30px", alignContent: "start", overflow: "hidden" }}>
        {layout.map(item => {
          const span = Math.min(Math.max(item.span, 3), 12);
          const offset = Math.min(Math.max(item.offset ?? 0, 0), 12 - span);
          return (
            <MasonryItem key={item.id} span={span} offset={offset}>
              <Widget widget={item.widget} data={data} todayK={todayK} noticeRows={notices.filter((n: any) => n.kind === "notice")} />
            </MasonryItem>
          );
        })}
        {layout.length === 0 && (
          <div style={{ gridColumn: "span 12", textAlign: "center", color: C.dim, paddingTop: 120, fontSize: 18 }}>
            Nothing published yet — a manager can arrange this board in Ops → Live Board.
          </div>
        )}
      </div>

      {/* Coretel Wire ticker */}
      <div style={{ background: "#12161a", borderTop: `2px solid ${C.accent}`, display: "flex", alignItems: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
        <span style={{ background: C.accent, color: "#fff", fontWeight: 800, fontSize: 14, letterSpacing: 2, padding: "15px 26px", flex: "none", textTransform: "uppercase", zIndex: 1 }}>Coretel Wire</span>
        <div style={{ overflow: "hidden", flex: 1 }}>
          <div className="wire-scroll" style={{ display: "inline-flex", gap: 64, paddingLeft: "100%", fontSize: 19, color: "#d5dbe2" }}>
            {[...tickerItems, ...tickerItems].map((t, i) => (
              <span key={i} style={{ display: "inline-flex", gap: 64, alignItems: "center" }}>
                {t}<span style={{ color: C.accent }}>◆</span>
              </span>
            ))}
          </div>
        </div>
        <style>{`
          .wire-scroll { animation: wire ${Math.max(30, tickerItems.length * 12)}s linear infinite; }
          @keyframes wire { from { transform: translateX(0); } to { transform: translateX(-50%); } }
          .board-vscroll { animation: vscroll 36s linear infinite; }
          @keyframes vscroll { 0%, 6% { transform: translateY(0); } 94%, 100% { transform: translateY(calc(-50% )); } }
        `}</style>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Masonry: the grid runs on fine 10px rows; each widget measures its real
// content height and claims exactly that many rows, and dense flow backfills
// gaps — compact widgets that pack tight, never row-blocks with voids.
const ROW_UNIT = 10;
const ROW_GAP = 26;
function MasonryItem({ span, offset, children }: { span: number; offset: number; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState(20);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const h = el.getBoundingClientRect().height;
      setRows(Math.max(4, Math.ceil((h + ROW_GAP) / (ROW_UNIT + ROW_GAP))));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const total = Math.min(span + offset, 12);
  return (
    <div style={{ gridColumn: `span ${total}`, gridRowEnd: `span ${rows}`, minWidth: 0, display: "flex", alignItems: "flex-start" }}>
      {offset > 0 && <div style={{ width: `${(offset / total) * 100}%`, flex: "none" }} aria-hidden />}
      <div ref={ref} style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ fontSize: 12.5, letterSpacing: 3, textTransform: "uppercase", color: C.accent, fontWeight: 800, marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ width: 7, height: 7, background: C.accent, transform: "rotate(45deg)", flex: "none" }} />
        {title}<span style={{ flex: 1, height: 1, background: C.cardBorder }} />
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

// News-room float (James, 4 Sep: "the line if longer than the space floats
// like the bottom") — when a widget's list is taller than its box, the
// content drifts upward on a slow loop instead of clipping.
function AutoScroll({ children, maxH }: { children: React.ReactNode; maxH: number }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [over, setOver] = useState(false);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const check = () => setOver(el.scrollHeight > el.clientHeight + 12);
    check();
    const t = setInterval(check, 15000); // content refreshes every 5 min
    return () => clearInterval(t);
  }, []);
  return (
    <div ref={boxRef} style={{ maxHeight: maxH, overflow: "hidden" }}>
      <div className={over ? "board-vscroll" : undefined}>
        {children}
        {over && <div style={{ height: 26 }} />}
        {over && <div aria-hidden>{children}</div>}
      </div>
    </div>
  );
}

// Shared list widget for plant + outage bookings — date tile, what, where, when.
function BookingListWidget({ title, rows, data, icon }: { title: string; rows: any[]; data: any; icon: string }) {
  const projName = (pid: string) => {
    const p = (data?.projects ?? []).find((x: any) => x.project_id === pid);
    return p?.sites?.site_name ?? p?.project_name ?? null;
  };
  const shown = rows.filter((r: any) => projName(r.project_id));
  return (
    <Panel title={title}>
      <div style={{ ...CARD, padding: "8px 16px" }}>
        <AutoScroll maxH={380}>
          {shown.map((r: any, i: number) => {
            const start = new Date(r.scheduled_start);
            return (
              <div key={i} style={{ display: "flex", gap: 14, alignItems: "center", padding: "12px 0", borderBottom: i < shown.length - 1 ? `1px solid ${C.line}` : "none" }}>
                <div style={{ width: 52, textAlign: "center", background: "rgba(255,120,68,.10)", border: "1px solid rgba(255,120,68,.28)", borderRadius: 10, padding: "6px 0", flex: "none" }}>
                  <div style={{ fontSize: 9, textTransform: "uppercase", color: C.accent, fontWeight: 800, letterSpacing: 1 }}>{format(start, "EEE")}</div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{format(start, "d")}</div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{icon} {r.title}</div>
                  <div style={{ fontSize: 11.5, color: C.mut }}>
                    {projName(r.project_id)} · {format(start, "HH:mm")}
                    {r.external_contact_name ? ` · ${r.external_contact_name}` : ""}
                  </div>
                </div>
              </div>
            );
          })}
          {shown.length === 0 && <Empty>Nothing booked in the next fortnight.</Empty>}
        </AutoScroll>
      </div>
    </Panel>
  );
}

function Widget({ widget, data, todayK, noticeRows }: { widget: string; data: any; todayK: string; noticeRows: any[] }) {
  switch (widget) {
    case "jobs":     return <JobsWidget data={data} todayK={todayK} />;
    case "week":     return <WeekWidget data={data} todayK={todayK} />;
    case "keydates": return <KeyDatesWidget data={data} todayK={todayK} />;
    case "calendar": return <CalendarWidget data={data} todayK={todayK} />;
    case "notices":  return <NoticesWidget rows={noticeRows} />;
    case "plant":    return <BookingListWidget title="Plant bookings" rows={data?.plant ?? []} data={data} icon="🏗" />;
    case "outages":  return <BookingListWidget title="Outages" rows={data?.outages ?? []} data={data} icon="🌙" />;
    case "hse":      return <HseWidget data={data} todayK={todayK} />;
    default:         return null;
  }
}

// ---- On site ---------------------------------------------------------------
function JobsWidget({ data, todayK }: { data: any; todayK: string }) {
  const projects = (data?.projects ?? []).filter((p: any) => !NOT_RUNNING.has(p.status));
  const subAgg = new Map<string, { done: number; total: number }>();
  for (const su of data?.subs ?? []) {
    const a = subAgg.get(su.project_id) ?? { done: 0, total: 0 };
    a.total += 1; if (su.done) a.done += 1;
    subAgg.set(su.project_id, a);
  }
  const todayLine = (pid: string) =>
    (data?.acts ?? []).find((a: any) => a.project_id === pid && a.in_build_plan &&
      a.start_date <= todayK && (a.end_date ?? a.start_date) >= todayK)
    ?? (data?.acts ?? []).filter((a: any) => a.project_id === pid && a.in_build_plan && a.start_date > todayK)
        .sort((x: any, y: any) => x.start_date.localeCompare(y.start_date))[0] ?? null;
  const crewFor = (pid: string) =>
    (data?.pcas ?? []).find((x: any) => x.project_id === pid &&
      x.assigned_from <= todayK && (x.assigned_to === null || x.assigned_to >= todayK))?.crews ?? null;
  const COLOURS = ["#FF7844", "#60A5FA", "#34D399", "#A78BFA", "#FBBF24"];

  return (
    <Panel title="On site">
      <AutoScroll maxH={560}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {projects.slice(0, 8).map((p: any, i: number) => {
          const line = todayLine(p.project_id);
          const agg = subAgg.get(p.project_id);
          const crew = crewFor(p.project_id);
          const col = crew?.colour_hex || COLOURS[i % COLOURS.length];
          const live = line && line.start_date <= todayK;
          return (
            <div key={p.project_id} style={{
              ...CARD,
              background: `radial-gradient(560px circle at 0% 0%, ${col}22, transparent 42%), linear-gradient(180deg, #1b222b, #151a20)`,
              padding: "18px 22px", position: "relative", overflow: "hidden",
            }}>
              <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 6, background: col, boxShadow: `2px 0 14px ${col}66` }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.3 }}>{p.sites?.site_name ?? p.project_name}</span>
                <span style={{ fontSize: 11.5, color: C.dim, fontFamily: "Consolas, monospace" }}>{p.project_code}</span>
              </div>
              {!line && (
                <div style={{ fontSize: 14, color: C.mut, marginTop: 5 }}>
                  {p.status === "mobilising" ? "Mobilising — crew heading in" : "Live on site"}
                </div>
              )}
              {line && (
                <div style={{ fontSize: 15, color: "#b8c0c8", marginTop: 5 }}>
                  {live ? "Today: " : `${format(keyToDate(line.start_date), "EEE d")}: `}
                  <b style={{ color: C.accent, fontWeight: 600 }}>{line.name}</b>
                </div>
              )}
              <div style={{ display: "flex", gap: 9, marginTop: 9, alignItems: "center", flexWrap: "wrap" }}>
                {crew && <Pill bg="rgba(96,165,250,.14)" fg="#93c5fd">{crew.crew_name}</Pill>}
                {line?.plant_needed?.length > 0 && <Pill bg="rgba(251,191,36,.12)" fg="#fcd34d">{line.plant_needed.slice(0, 2).join(" · ")}</Pill>}
                {agg && agg.total > 0 && <Pill bg="rgba(52,211,153,.12)" fg="#6ee7b7">{agg.done}/{agg.total} plan ticked</Pill>}
                {p.status === "mobilising" && <Pill bg="rgba(255,120,68,.12)" fg="#ffb494">mobilising</Pill>}
              </div>
              {agg && agg.total > 0 && (
                <div style={{ height: 6, borderRadius: 3, background: C.line, marginTop: 11, overflow: "hidden" }}>
                  <i style={{ display: "block", height: "100%", width: `${Math.round((agg.done / agg.total) * 100)}%`, background: col, borderRadius: 3 }} />
                </div>
              )}
            </div>
          );
        })}
        {projects.length === 0 && <Empty>No live jobs right now.</Empty>}
      </div>
      </AutoScroll>
    </Panel>
  );
}

// ---- The week --------------------------------------------------------------
function WeekWidget({ data, todayK }: { data: any; todayK: string }) {
  const weekStart = (() => { const d = keyToDate(todayK); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return localKey(d); })();
  const days = Array.from({ length: 7 }, (_, i) => addDaysKey(weekStart, i));
  const crews = (data?.crews ?? []).filter((c: any) => c.active !== false).slice(0, 7);
  const cellFor = (crewId: string, k: string) => {
    const pca = (data?.pcas ?? []).find((x: any) => x.crews?.crew_id === crewId &&
      x.assigned_from <= k && (x.assigned_to === null || x.assigned_to >= k) &&
      !NOT_RUNNING.has(x.projects?.status));
    return pca ? { name: (pca.projects?.sites?.site_name ?? pca.projects?.project_name ?? "").toUpperCase(), col: pca.crews?.colour_hex || "#60A5FA" } : null;
  };
  return (
    <Panel title="The week — who's where">
      <div style={{ ...CARD, padding: "16px 18px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead><tr>
            <th style={{ textAlign: "left", fontSize: 10.5, letterSpacing: 1, color: C.dim, textTransform: "uppercase", padding: "5px 4px" }}>Crew</th>
            {days.map(k => (
              <th key={k} style={{ fontSize: 10.5, letterSpacing: 1, color: k === todayK ? C.accent : C.dim, textTransform: "uppercase", padding: "5px 2px", textAlign: "center" }}>
                {format(keyToDate(k), "EEEEE")}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {crews.map((c: any) => (
              <tr key={c.crew_id}>
                <td style={{ padding: "11px 4px", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", borderTop: `1px solid ${C.line}` }}>{c.crew_name}</td>
                {days.map(k => {
                  const cell = cellFor(c.crew_id, k);
                  return (
                    <td key={k} style={{ padding: "10px 2px", textAlign: "center", borderTop: `1px solid ${C.line}` }}>
                      {cell
                        ? <span style={{ display: "inline-block", minWidth: 48, padding: "4px 7px", borderRadius: 7, fontSize: 10, fontWeight: 800, background: cell.col, color: "#10151a" }}>{cell.name.slice(0, 8)}</span>
                        : <span style={{ color: C.dim, fontSize: 10 }}>—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
            {crews.length === 0 && <tr><td colSpan={8}><Empty>No crews set up.</Empty></td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ---- Month calendar (company-wide) ----------------------------------------
function CalendarWidget({ data, todayK }: { data: any; todayK: string }) {
  const mStart = (() => { const d = keyToDate(todayK); return new Date(d.getFullYear(), d.getMonth(), 1); })();
  const first = new Date(mStart); first.setDate(first.getDate() - ((first.getDay() + 6) % 7));
  const monthEnd = new Date(mStart.getFullYear(), mStart.getMonth() + 1, 0);
  const cells: Date[] = [];
  for (let d = new Date(first); d <= monthEnd || (d.getDay() + 6) % 7 !== 0; d.setDate(d.getDate() + 1)) {
    cells.push(new Date(d));
    if (cells.length > 42) break;
  }
  const crewsOn = (k: string) =>
    (data?.pcas ?? []).filter((x: any) => x.assigned_from <= k && (x.assigned_to === null || x.assigned_to >= k) &&
      !NOT_RUNNING.has(x.projects?.status));
  return (
    <Panel title={`${format(mStart, "MMMM")} — all crews`}>
      <div style={{ ...CARD, padding: "16px 18px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 6 }}>
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
            <div key={d} style={{ textAlign: "center", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: C.dim, fontWeight: 700 }}>{d}</div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
          {cells.map((d, i) => {
            const k = localKey(d);
            const inMonth = d.getMonth() === mStart.getMonth();
            const on = inMonth ? crewsOn(k) : [];
            const isToday = k === todayK;
            const seen = new Set<string>();
            const chips = on.filter((x: any) => {
              const id = x.crews?.crew_id ?? x.pca_id;
              if (seen.has(id)) return false; seen.add(id); return true;
            }).slice(0, 3);
            return (
              <div key={i} style={{
                minHeight: 82, borderRadius: 10, padding: "7px 9px",
                background: !inMonth ? "transparent" : isToday ? "rgba(255,120,68,.10)" : "#1c232c",
                outline: isToday ? `2px solid ${C.accent}` : "none",
                opacity: inMonth ? 1 : 0.2,
              }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: isToday ? C.accent : inMonth ? C.text : C.dim }}>{d.getDate()}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 4 }}>
                  {chips.map((x: any, j: number) => {
                    const cc = x.crews?.colour_hex || "#60A5FA";
                    return (
                      <span key={j} style={{ fontSize: 9.5, fontWeight: 800, borderRadius: 5, padding: "3px 7px", background: cc, color: "#10151a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {(x.projects?.sites?.site_name ?? x.projects?.project_name ?? "").slice(0, 12)}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}

// ---- Key dates -------------------------------------------------------------
function KeyDatesWidget({ data, todayK }: { data: any; todayK: string }) {
  const projName = (pid: string) => {
    const p = (data?.projects ?? []).find((x: any) => x.project_id === pid);
    return p?.sites?.site_name ?? p?.project_name ?? null;
  };
  const rows = (data?.acts ?? [])
    .filter((a: any) => a.is_key && (a.end_date ?? a.start_date) >= todayK && projName(a.project_id))
    .sort((x: any, y: any) => x.start_date.localeCompare(y.start_date))
    .slice(0, 12);
  return (
    <Panel title="Key dates">
      <div style={{ ...CARD, padding: "8px 16px" }}>
        <AutoScroll maxH={380}>
        {rows.map((a: any, i: number) => (
          <div key={i} style={{ display: "flex", gap: 14, alignItems: "center", padding: "12px 0", borderBottom: i < rows.length - 1 ? `1px solid ${C.line}` : "none" }}>
            <div style={{ width: 52, textAlign: "center", background: "rgba(255,120,68,.10)", border: "1px solid rgba(255,120,68,.28)", borderRadius: 10, padding: "6px 0", flex: "none" }}>
              <div style={{ fontSize: 9, textTransform: "uppercase", color: C.accent, fontWeight: 800, letterSpacing: 1 }}>{format(keyToDate(a.start_date), "MMM")}</div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{format(keyToDate(a.start_date), "d")}</div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}><span style={{ color: C.accent }}>★</span> {projName(a.project_id)} — {a.name}</div>
              {a.plant_needed?.length > 0 && <div style={{ fontSize: 11, color: C.mut }}>{a.plant_needed.join(" · ")}</div>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty>No key dates flagged.</Empty>}
        </AutoScroll>
      </div>
    </Panel>
  );
}

// ---- Notices ---------------------------------------------------------------
function NoticesWidget({ rows }: { rows: any[] }) {
  return (
    <Panel title="From the office">
      <AutoScroll maxH={420}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {rows.slice(0, 10).map((n: any) => (
          <div key={n.notice_id} style={{ background: "linear-gradient(90deg, rgba(255,120,68,.12), transparent 70%)", border: "1px solid rgba(255,120,68,.3)", borderRadius: 14, padding: "13px 17px" }}>
            <div style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.45 }}>{n.body}</div>
          </div>
        ))}
        {rows.length === 0 && <Empty>No notices posted.</Empty>}
      </div>
      </AutoScroll>
    </Panel>
  );
}

// ---- Safety & defects (James, 4 Sep: "smaller pie charts on defects this
// month opened / closed. How many incidents") --------------------------------
function Donut({ a, b, colA, colB, centre, label }: {
  a: number; b: number; colA: string; colB: string; centre: string; label: string;
}) {
  const total = a + b;
  const r = 30, cir = 2 * Math.PI * r;
  const fracA = total > 0 ? a / total : 0;
  return (
    <svg width={92} height={92} viewBox="0 0 92 92">
      <circle cx={46} cy={46} r={r} fill="none" stroke={C.line} strokeWidth={11} />
      {total > 0 && (
        <>
          <circle cx={46} cy={46} r={r} fill="none" stroke={colB} strokeWidth={11}
            strokeDasharray={`${cir}`} transform="rotate(-90 46 46)" strokeLinecap="butt" />
          <circle cx={46} cy={46} r={r} fill="none" stroke={colA} strokeWidth={11}
            strokeDasharray={`${fracA * cir} ${cir}`} transform="rotate(-90 46 46)" strokeLinecap="butt" />
        </>
      )}
      <text x={46} y={44} textAnchor="middle" fill={C.text} fontSize={19} fontWeight={800}>{centre}</text>
      <text x={46} y={58} textAnchor="middle" fill={C.dim} fontSize={8} letterSpacing={0.5}>{label}</text>
    </svg>
  );
}

function HseWidget({ data, todayK }: { data: any; todayK: string }) {
  const monthK = todayK.slice(0, 7);
  const defects = data?.defects ?? [];
  const openedMonth = defects.filter((d: any) => (d.raised_at ?? "").slice(0, 7) === monthK).length;
  const closedMonth = defects.filter((d: any) => (d.closed_at ?? "").slice(0, 7) === monthK && d.status === "closed").length;
  const openNow = defects.filter((d: any) => d.status !== "closed" && d.status !== "cancelled").length;

  const incidents = data?.incidents ?? [];
  const incidentsMonth = incidents.filter((i: any) => (i.occurred_at ?? "").slice(0, 7) === monthK).length;
  const last = incidents[0]?.occurred_at ?? null;
  const daysSince = last
    ? Math.floor((Date.now() - new Date(last).getTime()) / 86400000)
    : null;

  const AMBER = "#f59e0b", GREEN = "#34D399";
  return (
    <Panel title="Safety & defects">
      <div style={{ ...CARD, padding: "20px 26px", display: "flex", alignItems: "center", justifyContent: "space-around", gap: 22, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Donut a={openedMonth} b={closedMonth} colA={AMBER} colB={GREEN} centre={String(openNow)} label="OPEN NOW" />
          <div style={{ fontSize: 11.5, lineHeight: 1.9 }}>
            <div><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: AMBER, marginRight: 7 }} />{openedMonth} raised this month</div>
            <div><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: GREEN, marginRight: 7 }} />{closedMonth} closed this month</div>
            <div style={{ color: C.dim, fontSize: 10 }}>defects</div>
          </div>
        </div>
        <div style={{ width: 1, alignSelf: "stretch", background: C.line }} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 38, fontWeight: 800, color: incidentsMonth > 0 ? "#f87171" : GREEN, lineHeight: 1 }}>{incidentsMonth}</div>
          <div style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: 1, color: C.mut, marginTop: 5 }}>incidents<br />this month</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 38, fontWeight: 800, color: GREEN, lineHeight: 1 }}>{daysSince ?? "—"}</div>
          <div style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: 1, color: C.mut, marginTop: 5 }}>days since<br />last incident</div>
        </div>
      </div>
    </Panel>
  );
}

function Pill({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 999, background: bg, color: fg, whiteSpace: "nowrap" }}>{children}</span>;
}
function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ color: C.dim, fontSize: 13, padding: "14px 4px" }}>{children}</div>;
}
